import { ModalInterface } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { Tooltip } from '@components/tooltip/tooltip'
import { useTaxonomyTranslation } from '@global/hooks/queries/taxonomy-translations'
import { t } from '@lingui/core/macro'
import { CircularProgress } from '@mui/material'
import {
  Button,
  DeleteModal,
  DonutProgressWrapper,
  ExportModal,
  FeedbackModal,
  LabeledData,
  TranslationDashboardHeader,
} from '@taxonomy-translation/components'
import { TranslationReviewModal } from '@taxonomy-translation/translation-review-modal'
import { formatSecondsToTime, getPercent, trackModalAction } from '@taxonomy-translation/utils'
import AISparkle from 'images/ai-sparkle.svg'
import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { TranslationDashboard } from './components'
import { MetricsCard } from './components/translation-dashboard/components'
import { useTableData } from './components/translation-dashboard/useTableData'

interface TaxonomyTranslationDashboardRouterParams {
  id: string
}

export const TaxonomyTranslationDashboard = () => {
  const { id } = useParams<TaxonomyTranslationDashboardRouterParams>()

  const feedbackModalRef = useRef<ModalInterface>(null)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)

  const { data, isLoading: translationFetching } = useTaxonomyTranslation(id)
  const { matchesMetrics, reviewProgress } = data || {}

  const { tableData, isLoading: tableDataFetching } = useTableData(id)

  const matchedPercentage = getPercent(reviewProgress?.match, matchesMetrics?.translations)
  const confirmedPercentage = getPercent(reviewProgress?.confirmed, matchesMetrics?.translations)
  const reviewedPercentage = getPercent(reviewProgress?.reviewed, matchesMetrics?.translations)
  const initialTranslationMatched = getPercent(
    matchesMetrics?.matches,
    matchesMetrics?.translations,
  )
  const hasReviewStarted = !!data?.reviewProgress?.reviewed

  const handleFeedback = () => {
    feedbackModalRef.current?.handleOpenModal()
  }

  const handleDelete = () => {
    setIsDeleteModalOpen(true)
  }

  const handleReviewOpen = () => {
    setIsReviewModalOpen(true)
    trackModalAction('opened')
  }

  const handleReviewClose = () => {
    setIsReviewModalOpen(false)
  }

  const headerHandlers = {
    handleFeedback,
    handleDelete,
  }

  if (translationFetching || tableDataFetching) {
    return (
      <div className="flex justify-center h-full ">
        <CircularProgress />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <AdminHeader>
        <TranslationDashboardHeader item={data} handlers={headerHandlers} />
      </AdminHeader>
      <div className="px-8 py-16">
        <div className="flex gap-8 mb-8 flex-wrap">
          <MetricsCard>
            <MetricsCard.Content>
              <div className="flex justify-between items-start w-full">
                <h2 className="font-roboto font-semibold text-3xl mr-8">
                  {reviewProgress?.noMatch} {t`items need your review`}
                </h2>
                <Button onClick={handleReviewOpen} disabled={!data?.matchesMetrics?.translations}>
                  {hasReviewStarted ? 'Continue review' : 'Begin review'}
                </Button>
              </div>
              <div className="flex items-center gap-9">
                <Tooltip
                  title={t`Percentage of items that have a match. This metric will increase as you review.`}
                  className="text-center"
                >
                  <DonutProgressWrapper label={t`matched`} value={matchedPercentage} />
                </Tooltip>
                <Tooltip
                  title={t`Percentage of initial translation matches that have been confirmed as accurate during review.`}
                  className="text-center"
                >
                  <DonutProgressWrapper label={t`confirmed`} value={confirmedPercentage} />
                </Tooltip>
                <Tooltip
                  title={t`Percentage of reviews completed. This metric will increase as you review.`}
                  className="text-center"
                >
                  <DonutProgressWrapper label={t`reviewed`} value={reviewedPercentage} />
                </Tooltip>
              </div>
            </MetricsCard.Content>
            <MetricsCard.LinearProgress progress={reviewedPercentage} />
          </MetricsCard>
          <MetricsCard>
            <MetricsCard.Content>
              <div className="flex font-roboto font-semibold text-3xl items-start gap-2">
                <h2>{t`Initial translation details`}</h2>
                <AISparkle />
              </div>
              <div className="flex items-center gap-9">
                <Tooltip
                  title={t`Time spent to complete the initial translation.`}
                  className="text-center"
                >
                  <LabeledData
                    value={formatSecondsToTime(matchesMetrics?.durationInSeconds || 0)}
                    label={t`translation time`}
                  />
                </Tooltip>
                <Tooltip
                  title={t`Percentage of items that were matched in the initial translation.`}
                  className="text-center"
                >
                  <LabeledData
                    value={`${initialTranslationMatched}%`}
                    label={t`initially matched`}
                  />
                </Tooltip>
                <Tooltip
                  title={t`Number of items that were matched in the initial translation.`}
                  className="text-center"
                >
                  <LabeledData
                    value={`${matchesMetrics?.matches}/${matchesMetrics?.translations}`}
                    label={t`initially matched`}
                  />
                </Tooltip>
              </div>
            </MetricsCard.Content>
            <div className="rounded-bl-lg rounded-br-lg overflow-hidden h-[7px]" />
          </MetricsCard>
        </div>
        <TranslationDashboard data={tableData} inputs={data?.inputs || []} />
      </div>
      <FeedbackModal ref={feedbackModalRef} />
      {isDeleteModalOpen && !!data && (
        <DeleteModal
          onClose={() => {
            setIsDeleteModalOpen(false)
          }}
          translation={data}
        />
      )}
      {isExportModalOpen && !!data && (
        <ExportModal
          translation={data}
          onClose={() => {
            setIsExportModalOpen(false)
          }}
        />
      )}
      {!!data?.matchesMetrics?.translations && (
        <TranslationReviewModal
          translation={data}
          isOpen={isReviewModalOpen}
          isReviewed={!!data?.reviewProgress?.reviewed}
          onClose={handleReviewClose}
        />
      )}
    </div>
  )
}
